import { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../../shared/constants/env';

// ICEGate uses 6-char codes for Indian ports (LOCODE + digit suffix)
function toIcegateCode(locode: string): string {
  return locode.startsWith('IN') ? `${locode}1` : locode;
}

const PORT_NAMES: Record<string, { name: string; country: string; countryCode: string }> = {
  INMAA: { name: 'Chennai Sea Port', country: 'India', countryCode: 'IN' },
  INBOM: { name: 'Mumbai Port Trust', country: 'India', countryCode: 'IN' },
  INNSA: { name: 'Nhava Sheva (JNPT)', country: 'India', countryCode: 'IN' },
  INCCU: { name: 'Kolkata Port', country: 'India', countryCode: 'IN' },
  CNSHA: { name: 'Port of Shanghai', country: 'China', countryCode: 'CN' },
  CNSZX: { name: 'Port of Shenzhen', country: 'China', countryCode: 'CN' },
  CNNGB: { name: 'Port of Ningbo', country: 'China', countryCode: 'CN' },
  CNTJN: { name: 'Port of Tianjin', country: 'China', countryCode: 'CN' },
  CNGZU: { name: 'Port of Guangzhou', country: 'China', countryCode: 'CN' },
  USLAX: { name: 'Port of Los Angeles', country: 'United States', countryCode: 'US' },
  USNYC: { name: 'Port of New York', country: 'United States', countryCode: 'US' },
  USHOU: { name: 'Port of Houston', country: 'United States', countryCode: 'US' },
  USSAV: { name: 'Port of Savannah', country: 'United States', countryCode: 'US' },
  DEHAM: { name: 'Port of Hamburg', country: 'Germany', countryCode: 'DE' },
  NLRTM: { name: 'Port of Rotterdam', country: 'Netherlands', countryCode: 'NL' },
  BEANR: { name: 'Port of Antwerp', country: 'Belgium', countryCode: 'BE' },
  GBFXT: { name: 'Port of Felixstowe', country: 'United Kingdom', countryCode: 'GB' },
  SGSIN: { name: 'Port of Singapore', country: 'Singapore', countryCode: 'SG' },
  AEJEA: { name: 'Jebel Ali Port', country: 'United Arab Emirates', countryCode: 'AE' },
  JPYOK: { name: 'Port of Yokohama', country: 'Japan', countryCode: 'JP' },
  KRPUS: { name: 'Port of Busan', country: 'South Korea', countryCode: 'KR' },
  AUSYD: { name: 'Port of Sydney', country: 'Australia', countryCode: 'AU' },
  VNSGN: { name: 'Port of Ho Chi Minh', country: 'Vietnam', countryCode: 'VN' },
  THLCH: { name: 'Laem Chabang Port', country: 'Thailand', countryCode: 'TH' },
  IDJKT: { name: 'Tanjung Priok Port', country: 'Indonesia', countryCode: 'ID' },
};

// Tvikla's standard exporter profile — update via company settings in production
const TVIKLA_EXPORTER = {
  iec: 'AAPCT7878Q',
  gstin: '33AAPCT7878Q1ZC',
  name: 'TVIKLA EXPORTS PRIVATE LIMITED',
  address_line1: 'Level 12, Prestige Tech Park, Marathahalli',
  address_line2: 'Outer Ring Road',
  city: 'Bengaluru',
  state: 'Karnataka',
  state_code: '29',
  pin: '560103',
  email: 'exports@tvikla.com',
  phone: '+91 80 4567 8901',
  ad_code: '1234567',
  customs_broker: { name: '', licence_no: '' },
};

export type ShippingBillStatus =
  | 'not_started'
  | 'drafting'
  | 'drafted'
  | 'vessel_updated'
  | 'filed'
  | 'leo_granted';

export interface ShippingBillState {
  status: ShippingBillStatus;
  sbNumber?: string;
  fileName?: string;
  fileUrl?: string;
  generatedAt?: string;
  vesselName?: string;
  voyageNumber?: string;
  filedAt?: string;
  leoAt?: string;
}

export interface VesselUpdate {
  vesselName: string;
  voyageNumber: string;
  rotationNumber?: string;
  etd?: string;
}

function buildSBPayload(params: {
  orderId: string;
  customerName: string;
  originPort: string;
  destinationPort: string;
  currency: string;
  sbDate: string;
  vessel?: VesselUpdate;
}): Record<string, unknown> {
  const { orderId, customerName, originPort, destinationPort, currency, sbDate, vessel } = params;
  const origin = PORT_NAMES[originPort] ?? { name: originPort, country: 'India', countryCode: 'IN' };
  const dest = PORT_NAMES[destinationPort] ?? { name: destinationPort, country: 'Unknown', countryCode: 'XX' };
  const isDraft = !vessel;

  return {
    sb_type: 'FREE',
    sb_date: sbDate,
    exporter: TVIKLA_EXPORTER,
    consignee: {
      name: customerName,
      address: 'As per commercial invoice',
      country: dest.country,
      country_code: dest.countryCode,
    },
    shipment: {
      port_of_loading_code: toIcegateCode(originPort),
      port_of_loading_name: origin.name,
      port_of_discharge_code: destinationPort,
      port_of_discharge_name: dest.name,
      country_of_destination: dest.country,
      country_code_destination: dest.countryCode,
      vessel_name: vessel?.vesselName ?? 'TBD',
      voyage_number: vessel?.voyageNumber ?? 'TBD',
      rotation_number: vessel?.rotationNumber ?? '',
      etd: vessel?.etd ?? '',
      nature_of_contract: 'FOB',
      nature_of_cargo: 'Containerized',
      gross_weight_kg: 0,
      net_weight_kg: 0,
      containers: [],
    },
    invoice: {
      invoice_number: `DRAFT-${orderId}`,
      invoice_date: sbDate,
      currency_code: currency,
      exchange_rate: 83.5,
      freight_fcy: 0,
      insurance_fcy: 0,
    },
    items: [
      {
        serial_number: 1,
        description: isDraft
          ? `Goods as per Order ${orderId} — item details to be confirmed before filing`
          : `Goods as per Order ${orderId}`,
        hs_code: '99999999',
        number_of_packages: 1,
        package_type: 'CTNS',
        quantity: 1,
        unit: 'NOS',
        unit_price_fcy: 0,
        country_of_origin: 'IN',
        scheme_code: 'RODTEP',
      },
    ],
    igst: {
      export_with_igst: false,
      export_under_lut: true,
      lut_bond_reference: '',
    },
    order_id: orderId,
    remarks: isDraft ? 'DRAFT — vessel and item details pending. Not for ICEGATE submission.' : '',
    save: true,
  };
}

interface UseShippingBillParams {
  orderId: string;
  customerName: string;
  originPort: string;
  destinationPort: string;
  currency?: string;
}

export function useShippingBill({
  orderId,
  customerName,
  originPort,
  destinationPort,
  currency = 'USD',
}: UseShippingBillParams) {
  const storageKey = `scheduling:sb:${orderId}`;
  const apiOrigin = API_BASE_URL.replace(/\/graphql\/?$/, '');

  const loadPersistedState = (): ShippingBillState => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed.status === 'string') return parsed as ShippingBillState;
      }
    } catch {
      // ignore
    }
    return { status: 'not_started' };
  };

  const [state, setState] = useState<ShippingBillState>(loadPersistedState);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setState(loadPersistedState());
    setError(null);
  }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
      // ignore storage errors
    }
  }, [state, storageKey]);

  const callApi = useCallback(
    async (payload: Record<string, unknown>) => {
      const resp = await fetch(`${apiOrigin}/api/v1/documents/shipping-bill/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const errText = await resp.text().catch(() => resp.statusText);
        throw new Error(errText || 'Shipping bill generation failed');
      }
      const data = await resp.json();
      return {
        sbNumber: (data.sb_number ?? data.sbNumber ?? '') as string,
        fileName: (data.file_name ?? data.fileName ?? '') as string,
        fileUrl: (data.file_url ?? data.fileUrl ?? '') as string,
      };
    },
    [apiOrigin],
  );

  const generateDraft = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setState((prev) => ({ ...prev, status: 'drafting' }));
    try {
      const today = new Date().toISOString().slice(0, 10);
      const payload = buildSBPayload({ orderId, customerName, originPort, destinationPort, currency, sbDate: today });
      const result = await callApi(payload);
      setState({
        status: 'drafted',
        sbNumber: result.sbNumber,
        fileName: result.fileName,
        fileUrl: result.fileUrl,
        generatedAt: new Date().toISOString(),
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      setState((prev) => ({ ...prev, status: 'not_started' }));
    } finally {
      setIsLoading(false);
    }
  }, [orderId, customerName, originPort, destinationPort, currency, callApi]);

  const updateWithVessel = useCallback(
    async (vessel: VesselUpdate) => {
      setIsLoading(true);
      setError(null);
      try {
        const today = new Date().toISOString().slice(0, 10);
        const payload = buildSBPayload({ orderId, customerName, originPort, destinationPort, currency, sbDate: today, vessel });
        const result = await callApi(payload);
        setState((prev) => ({
          ...prev,
          status: 'vessel_updated',
          sbNumber: result.sbNumber,
          fileName: result.fileName,
          fileUrl: result.fileUrl,
          generatedAt: new Date().toISOString(),
          vesselName: vessel.vesselName,
          voyageNumber: vessel.voyageNumber,
        }));
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
      } finally {
        setIsLoading(false);
      }
    },
    [orderId, customerName, originPort, destinationPort, currency, callApi],
  );

  const markFiled = useCallback(() => {
    setState((prev) => ({ ...prev, status: 'filed', filedAt: new Date().toISOString() }));
  }, []);

  const markLeoGranted = useCallback(() => {
    setState((prev) => ({ ...prev, status: 'leo_granted', leoAt: new Date().toISOString() }));
  }, []);

  return { state, isLoading, error, generateDraft, updateWithVessel, markFiled, markLeoGranted };
}
